import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { apiMessage } from '@/lib/pricing';
import { useGlobalAdminStatus } from '@/hooks/useGlobalAdminStatus';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import MultiSearchableSelect from '@/components/common/MultiSearchableSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

type Money = { monthly: string; currency: string; free_until?: string | null };

type FleetServer = {
  automation_server_id: string;
  name: string;
  domain: string | null;
  server_type: 'byos' | 'hetzner';
  hetzner_server_type: string | null;
  location: string | null;
  provisioning_status: string;
  active: boolean;
  bailey_version: string | null;
  bailey_version_reported_at: string | null;
  created_at: string;
  customer_price: Money | null;
  bitswan_cost: Money | null;
  settings: { release_channels: string[] };
};

type FleetCustomer = {
  org_id: string;
  name: string;
  currency: string | null;
  servers: FleetServer[];
  monthly_customer_total: string | null;
  monthly_bitswan_cost_eur: string | null;
};

type Channel = { value: string; label: string };

type Fleet = { customers: FleetCustomer[]; hetzner_costs_known: boolean; channels: Channel[] };

export const FLEET_QUERY_KEY = ['global-admin-servers'];

function money(value: string | null | undefined, currency: string | null | undefined) {
  if (value == null) return '—';
  const amount = Number(value);
  if (!currency) return amount.toFixed(2);
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
}

function hosting(server: FleetServer) {
  if (server.server_type !== 'hetzner') return 'Their own';
  return [server.hetzner_server_type, server.location].filter(Boolean).join(' · ') || 'Cloud';
}

function status(server: FleetServer) {
  if (server.active) return <Badge variant="secondary">running</Badge>;
  if (server.server_type === 'hetzner') return <Badge variant="outline">{server.provisioning_status}</Badge>;
  return <Badge variant="outline">not registered</Badge>;
}

export function ServerFleet() {
  const { isGlobalAdmin } = useGlobalAdminStatus();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: FLEET_QUERY_KEY,
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/servers/')).data as Fleet;
    },
  });

  const setting = useMutation({
    mutationFn: async ({ id, settings }: { id: string; settings: Partial<FleetServer['settings']> }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.put(`/global-admin/servers/${id}/`, { settings });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FLEET_QUERY_KEY }),
    onError: (err: any) => toast.error(apiMessage(err)),
  });

  const customers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!data) return [];
    if (!needle) return data.customers;
    return data.customers
      .map((customer) => customer.name.toLowerCase().includes(needle)
        ? customer
        : { ...customer, servers: customer.servers.filter((server) =>
            [server.name, server.domain].some((text) => text?.toLowerCase().includes(needle))) })
      .filter((customer) => customer.servers.length > 0);
  }, [data, search]);

  const serverCount = data?.customers.reduce((n, customer) => n + customer.servers.length, 0) ?? 0;
  const testers = data?.customers.reduce(
    (n, customer) => n + customer.servers.filter((server) => server.settings.release_channels.includes('pull-request')).length, 0) ?? 0;
  const channelOptions = (data?.channels ?? []).map((channel) => ({ value: channel.value, label: channel.label }));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardDescription>Customers with servers</CardDescription></CardHeader>
          <CardContent className="text-2xl font-semibold" data-testid="fleet-customers">{data?.customers.length ?? '—'}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Servers</CardDescription></CardHeader>
          <CardContent className="text-2xl font-semibold" data-testid="fleet-servers">{data ? serverCount : '—'}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>On the pull-request channel</CardDescription></CardHeader>
          <CardContent className="text-2xl font-semibold" data-testid="fleet-testers">{data ? testers : '—'}</CardContent>
        </Card>
      </div>

      <Input
        placeholder="Search customers and servers"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="max-w-sm"
      />

      {data && !data.hetzner_costs_known && (
        <p className="text-sm text-muted-foreground">
          Hetzner's prices could not be read, so what cloud servers cost BitSwan is not shown.
        </p>
      )}
      {isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
      {error && <p className="text-sm text-destructive">{apiMessage(error)}</p>}
      {data && customers.length === 0 && <p className="text-sm text-muted-foreground">No servers match.</p>}

      {customers.map((customer) => (
        <Card key={customer.org_id} data-testid={`fleet-customer-${customer.org_id}`}>
          <CardHeader>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <CardTitle className="text-lg">
                <Link to={`/customers/${customer.org_id}`} className="hover:underline">{customer.name}</Link>
              </CardTitle>
              <CardDescription>
                Bills {money(customer.monthly_customer_total, customer.currency)} a month
                {customer.monthly_bitswan_cost_eur != null && <> · costs BitSwan {money(customer.monthly_bitswan_cost_eur, 'EUR')} at Hetzner</>}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Server</TableHead>
                  <TableHead>Hosting</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead className="text-right">Customer pays / month</TableHead>
                  <TableHead className="text-right">BitSwan pays / month</TableHead>
                  <TableHead className="w-56">Release channels</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customer.servers.map((server) => {
                  const saving = setting.isPending && setting.variables?.id === server.automation_server_id;
                  return (
                    <TableRow key={server.automation_server_id} data-testid={`fleet-server-${server.automation_server_id}`}>
                      <TableCell>
                        <div className="font-medium">{server.name}</div>
                        {server.domain && <div className="text-xs text-muted-foreground">{server.domain}</div>}
                      </TableCell>
                      <TableCell>{hosting(server)}</TableCell>
                      <TableCell>{status(server)}</TableCell>
                      <TableCell className="font-mono text-xs">{server.bailey_version ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        {money(server.customer_price?.monthly, server.customer_price?.currency)}
                        {server.customer_price?.free_until && (
                          <div className="text-xs text-muted-foreground">free until {server.customer_price.free_until}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{money(server.bitswan_cost?.monthly, server.bitswan_cost?.currency)}</TableCell>
                      <TableCell>
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <MultiSearchableSelect
                            options={channelOptions}
                            value={server.settings.release_channels}
                            minimum={1}
                            disabled={!isGlobalAdmin}
                            placeholder="No channel"
                            searchPlaceholder="Search channels…"
                            testId={`fleet-channels-${server.automation_server_id}`}
                            onChange={(channels) => setting.mutate({
                              id: server.automation_server_id,
                              settings: { release_channels: channels },
                            })}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
